/**
 * ManuscriptAiImportDialog
 *
 * Drag-drop import for .docx / .txt files into the CURRENT project.
 * After chapters are replaced it runs a background AI pipeline that
 * auto-populates Plot Builder, Synopsis, and World Builder.
 *
 * Flow:
 * 1. User drops / picks a file.
 * 2. If the project already has chapters a confirmation step is shown.
 * 3. On confirm → POST /api/projects/{pid}/import-ai-pipeline
 * 4. Progress screen polls GET .../ai-import-pipeline/{job_id} until done.
 * 5. Done screen offers to reload the project.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  FileText, FileUp, Loader2, Check, AlertTriangle, Sparkles,
  Map, Users, BookOpen, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ACCEPT = '.docx,.txt,.md,.markdown';
const MAX_BYTES = 20 * 1024 * 1024;

const PIPELINE_STEPS = [
  { key: 'plot_extraction', icon: Map,     label: 'Extracting plot structure' },
  { key: 'synopsis',        icon: BookOpen, label: 'Generating synopsis'       },
  { key: 'world_builder',   icon: Globe,   label: 'Deducing world details'    },
];

export default function ManuscriptAiImportDialog({ open, onOpenChange }) {
  const { project, chapters = [], loadProject } = usePW();
  const [file,          setFile]    = useState(null);
  const [stage,         setStage]   = useState('pick');   // pick | confirm | running | done | error
  const [jobId,         setJobId]   = useState(null);
  const [jobDoc,        setJobDoc]  = useState(null);
  const [chapterCount,  setChapterCount] = useState(0);
  const [totalWords,    setTotalWords]   = useState(0);
  const [errorMsg,      setErrorMsg]     = useState('');
  const pollRef = useRef(null);
  const inputRef = useRef(null);

  const hasExistingChapters = chapters.some(
    (c) => (c.word_count || 0) > 0 || (c.content || '').length > 0,
  );

  // Reset on close
  useEffect(() => {
    if (!open) {
      setFile(null); setStage('pick'); setJobId(null);
      setJobDoc(null); setErrorMsg('');
      clearInterval(pollRef.current);
    }
  }, [open]);

  const pickFile = useCallback((f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`File too large (max ${MAX_BYTES / 1_000_000 | 0} MB).`);
      return;
    }
    setFile(f);
    setStage('confirm');
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    pickFile(e.dataTransfer?.files?.[0]);
  }, [pickFile]);

  const doImport = useCallback(async () => {
    if (!file || !project?.id) return;
    setStage('running');
    const fd = new FormData();
    fd.append('file', file, file.name);
    try {
      const { data } = await axios.post(
        `${API}/projects/${project.id}/import-ai-pipeline`,
        fd,
        { withCredentials: true, timeout: 60000 },
      );
      setJobId(data.job_id);
      setChapterCount(data.chapter_count);
      setTotalWords(data.total_words);
      setJobDoc({ status: 'running', completed_steps: [], failed_steps: [], current_step: 'starting' });

      // Start polling
      pollRef.current = setInterval(async () => {
        try {
          const { data: job } = await axios.get(
            `${API}/projects/${project.id}/ai-import-pipeline/${data.job_id}`,
            { withCredentials: true },
          );
          setJobDoc(job);
          if (job.status === 'done') {
            clearInterval(pollRef.current);
            setStage('done');
            await loadProject(project.id, { silent: true });
            // ── Success toast with chapter count + quick-nav ──
            toast.success(
              `${data.chapter_count} chapters imported — AI analysis complete`,
              {
                description: 'Plot, synopsis, and world details have been extracted. Review them in their respective tools.',
                action: {
                  label: 'View chapters',
                  onClick: () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: 'chapters' })),
                },
                duration: 8000,
              },
            );
          }
        } catch { /* silent */ }
      }, 2500);
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || 'Import failed — please try again.');
      setStage('error');
    }
  }, [file, project?.id, loadProject]);

  const completedKeys = jobDoc?.completed_steps || [];
  const failedKeys    = jobDoc?.failed_steps    || [];
  const currentKey    = jobDoc?.current_step;

  const stepStatus = (key) => {
    if (completedKeys.includes(key)) return 'done';
    if (failedKeys.includes(key))    return 'failed';
    if (currentKey === key)          return 'running';
    return 'pending';
  };

  const progressPct = PIPELINE_STEPS.length
    ? Math.round((completedKeys.length / PIPELINE_STEPS.length) * 100)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ai-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Sparkles className="h-4 w-4 text-primary" />
            AI Manuscript Import
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Drop a .docx or .txt file. The AI will extract plot, synopsis, and world details automatically.
          </DialogDescription>
          {/* Always show which project will receive the import */}
          {project && (
            <div className="mt-1.5 flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2.5 py-1.5 text-[11.5px]">
              <BookOpen className="h-3 w-3 shrink-0 text-primary" />
              <span className="text-muted-foreground">Importing into:</span>
              <span className="font-semibold text-foreground truncate">{project.title || project.name || 'current project'}</span>
            </div>
          )}
        </DialogHeader>

        {/* ── Pick / Confirm ── */}
        {(stage === 'pick' || stage === 'confirm') && (
          <>
            {stage === 'pick' ? (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="ai-import-drop-zone"
                className="mt-2 flex h-36 w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-card/50 text-center transition-colors hover:border-primary hover:bg-primary/5"
              >
                <FileUp className="h-7 w-7 text-muted-foreground" />
                <p className="text-[12.5px] text-muted-foreground">
                  Drop your manuscript here, or click to browse
                </p>
                <p className="text-[10.5px] text-muted-foreground/60">{ACCEPT}</p>
              </button>
            ) : (
              <div className="mt-2 rounded-xl border border-border bg-secondary/30 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 shrink-0 text-primary" />
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold">{file?.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {((file?.size || 0) / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>

                {hasExistingChapters && (
                  <div
                    className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50/80 p-3 text-[12px] dark:border-amber-700/40 dark:bg-amber-950/30"
                    data-testid="ai-import-replace-warning"
                  >
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <span className="text-amber-800 dark:text-amber-300">
                      This will <strong>replace all {chapters.length} existing chapters</strong> in this project.
                      Your variables, characters, and settings will be preserved.
                    </span>
                  </div>
                )}

                <p className="text-[11.5px] text-muted-foreground">
                  After import, the AI will automatically fill in the Plot Builder,
                  Synopsis, and World Builder from your manuscript content.
                </p>
              </div>
            )}

            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              data-testid="ai-import-file-input"
              onChange={(e) => pickFile(e.target.files?.[0])}
            />

            <div className="flex justify-end gap-2 mt-2">
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              {stage === 'pick' && (
                <Button
                  size="sm"
                  onClick={() => inputRef.current?.click()}
                  data-testid="ai-import-browse-btn"
                  className="gap-1.5"
                >
                  <FileUp className="h-3.5 w-3.5" /> Browse file
                </Button>
              )}
              {stage === 'confirm' && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setFile(null); setStage('pick'); }}
                  >
                    Change file
                  </Button>
                  <Button
                    size="sm"
                    onClick={doImport}
                    data-testid="ai-import-confirm-btn"
                    className="gap-1.5"
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Import + run AI
                  </Button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Running ── */}
        {stage === 'running' && (
          <div className="space-y-4 py-2" data-testid="ai-import-progress">
            <div>
              <div className="mb-1 flex items-center justify-between text-[11.5px]">
                <span className="font-semibold text-foreground">
                  {chapterCount} chapters imported · {totalWords.toLocaleString()} words
                </span>
                <span className="text-muted-foreground">Running AI pipeline…</span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
            </div>

            <div className="space-y-2">
              {PIPELINE_STEPS.map(({ key, icon: StepIcon, label }) => {
                const status = stepStatus(key);
                return (
                  <div
                    key={key}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border px-3 py-2 text-[12px] transition-all',
                      status === 'done'    && 'border-emerald-300/60 bg-emerald-50/60 dark:border-emerald-700/40 dark:bg-emerald-950/20',
                      status === 'running' && 'border-primary/40 bg-primary/5',
                      status === 'failed'  && 'border-amber-300/60 bg-amber-50/60 dark:border-amber-700/40',
                      status === 'pending' && 'border-border bg-secondary/20 opacity-50',
                    )}
                    data-testid={`ai-import-step-${key}`}
                  >
                    {status === 'done'    && <Check   className="h-4 w-4 shrink-0 text-emerald-600" />}
                    {status === 'running' && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary" />}
                    {status === 'failed'  && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />}
                    {status === 'pending' && <StepIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className={cn(
                      status === 'done'    ? 'text-emerald-700 dark:text-emerald-400' :
                      status === 'running' ? 'font-semibold text-foreground' :
                      status === 'failed'  ? 'text-amber-700 dark:text-amber-400' :
                      'text-muted-foreground',
                    )}>
                      {label}
                      {status === 'failed' && ' (skipped — edit manually)'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Done ── */}
        {stage === 'done' && (
          <div className="space-y-4 py-2" data-testid="ai-import-done">
            <div className="flex items-start gap-3 rounded-xl border border-emerald-300/60 bg-emerald-50/60 p-4 dark:border-emerald-700/40 dark:bg-emerald-950/20">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
              <div>
                <p className="font-semibold text-emerald-800 dark:text-emerald-400">
                  Import complete!
                </p>
                <p className="mt-0.5 text-[12px] text-muted-foreground">
                  {chapterCount} chapters imported · {totalWords.toLocaleString()} words · Plot, synopsis, and world details extracted.
                </p>
              </div>
            </div>
            <p className="text-[12px] text-muted-foreground">
              Review the extracted data in the Plot Builder, Synopsis Builder, and World Builder — the AI
              may have made some assumptions. Edit anything that doesn't match your vision.
            </p>
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => onOpenChange(false)}
                data-testid="ai-import-done-btn"
              >
                Start writing
              </Button>
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {stage === 'error' && (
          <div className="space-y-3 py-2" data-testid="ai-import-error">
            <div className="flex items-start gap-2 rounded-lg border border-rose-300/60 bg-rose-50/60 p-3 text-[12px] dark:border-rose-700/40 dark:bg-rose-950/20">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" />
              <span className="text-rose-800 dark:text-rose-400">{errorMsg}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              <Button size="sm" onClick={() => setStage('confirm')}>Try again</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
